/**
 * <StudyHub> — the Deck Hub landing page (`/study`).
 * Bento layout: modules on the left, session recommender in the center,
 * streak + mastery heatmap on the right.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/apiClient';
import { getStudyHub, getDuelInbox } from '../lib/studyApi';
import { STUDY_THEME, paperBackground } from '../components/study/studyTheme';
import InviteBuddyModal from '../components/study/InviteBuddyModal';

const cardStyle = {
  background: 'white',
  borderRadius: 14,
  boxShadow: STUDY_THEME.shadow,
  padding: 24,
};

const sectionLabel = {
  fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 12,
};

function DeckRow({ deck, onStart, onDuel, onBuddyInvite, canGenerate, onRegenerate, isRegenerating }) {
  const mastery = deck.mastery_pct || 0;
  const barColor = mastery >= 70 ? '#16A34A' : mastery >= 40 ? STUDY_THEME.goldDark : '#F97316';
  const extraCols = (canGenerate ? 1 : 0) + 3; // regen? + buddy + duel + study
  return (
    <div
      data-testid={`deck-row-${deck.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: `1fr ${'auto '.repeat(extraCols).trim()}`,
        gap: 12, alignItems: 'center',
        padding: '16px 18px', borderRadius: 10,
        background: STUDY_THEME.paper, marginBottom: 10,
        border: `1px solid ${STUDY_THEME.line}`,
      }}
    >
      <div>
        <div style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 18, fontWeight: 600, marginBottom: 4,
        }}>
          {deck.name}
        </div>
        <div style={{ fontSize: 13, color: STUDY_THEME.muted }}>
          {deck.card_count} cards
          {deck.due_count > 0 && (
            <>
              {' · '}
              <span style={{ color: '#F97316', fontWeight: 600 }}>
                {deck.due_count} due
              </span>
            </>
          )}
          {deck.module && <> · <em>{deck.module}</em></>}
        </div>
        <div style={{
          marginTop: 10, height: 6, background: STUDY_THEME.line,
          borderRadius: 999, overflow: 'hidden',
        }}>
          <div style={{
            width: `${mastery}%`, height: '100%',
            background: barColor, transition: 'width 0.4s',
          }} />
        </div>
      </div>
      {canGenerate && onRegenerate && (
        <button
          onClick={() => onRegenerate(deck)}
          disabled={isRegenerating}
          data-testid={`deck-regen-btn-${deck.id}`}
          title="Regenerate this deck's cards using Claude from the module's lesson content"
          style={{
            padding: '10px 14px', fontSize: 13, fontWeight: 600,
            background: 'white', color: STUDY_THEME.inkSoft,
            border: `1px solid ${STUDY_THEME.line}`, borderRadius: 8,
            cursor: isRegenerating ? 'wait' : 'pointer', fontFamily: 'inherit',
            opacity: isRegenerating ? 0.6 : 1,
          }}
        >
          {isRegenerating ? '🌀 …' : '🔄 Regen'}
        </button>
      )}
      <button
        onClick={() => onDuel(deck.id)}
        data-testid={`deck-duel-btn-${deck.id}`}
        title="Duel the AI on this deck"
        style={{
          padding: '10px 14px', fontSize: 13, fontWeight: 700,
          background: 'white', color: STUDY_THEME.goldDeep,
          border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        ⚔️ AI
      </button>
      <button
        onClick={() => onBuddyInvite(deck)}
        data-testid={`deck-buddy-btn-${deck.id}`}
        title="Challenge a colleague to a peer duel"
        style={{
          padding: '10px 14px', fontSize: 13, fontWeight: 700,
          background: 'white', color: '#7C3AED',
          border: '1px solid #C4B5FD', borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        🥊 Colleague
      </button>
      <button
        onClick={() => onStart(deck.id)}
        data-testid={`deck-start-btn-${deck.id}`}
        style={{
          padding: '10px 20px', fontSize: 14, fontWeight: 700,
          background: STUDY_THEME.gold, color: STUDY_THEME.ink,
          border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Study →
      </button>
    </div>
  );
}

function StreakCard({ streak }) {
  return (
    <div style={{ ...cardStyle, textAlign: 'center' }}>
      <div style={sectionLabel}>Study Streak</div>
      <div style={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: 56, fontWeight: 700, lineHeight: 1,
        color: streak > 0 ? STUDY_THEME.goldDark : STUDY_THEME.muted,
      }}>
        {streak}
      </div>
      <div style={{ fontSize: 13, color: STUDY_THEME.muted, marginTop: 6 }}>
        {streak === 1 ? 'day in a row' : 'days in a row'}
      </div>
      {streak >= 3 && (
        <div style={{ marginTop: 12, fontSize: 20 }}>🔥</div>
      )}
    </div>
  );
}

export default function StudyHub() {
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [err, setErr] = useState('');
  const [role, setRole] = useState('');
  const [generating, setGenerating] = useState('');
  const [genErr, setGenErr] = useState('');
  const [inboxSummary, setInboxSummary] = useState({ incoming_count: 0, active_count: 0 });
  const [inviteDeck, setInviteDeck] = useState(null);

  const refresh = () => getStudyHub().then(setHub).catch((e) => setErr(e.message || 'Failed to load Deck Hub'));
  const refreshInbox = () => getDuelInbox()
    .then((d) => setInboxSummary({
      incoming_count: d.incoming_count || 0,
      active_count: (d.active || []).length,
    }))
    .catch(() => {});

  useEffect(() => {
    refresh();
    refreshInbox();
    api.get('/api/auth/me').then((r) => setRole((r?.data?.role || '').toLowerCase())).catch(() => {});
    const t = setInterval(refreshInbox, 15000);
    return () => clearInterval(t);
  }, []);

  const canGenerate = ['developer', 'institution_admin', 'teacher'].includes(role);

  const generateFor = async (moduleId) => {
    setGenerating(moduleId);
    setGenErr('');
    try {
      await api.post('/api/study/decks/generate', { module_id: moduleId });
      await refresh();
    } catch (e) {
      setGenErr(e?.response?.data?.detail || e.message || 'Generation failed');
    } finally {
      setGenerating('');
    }
  };

  const startSession = (deckId) => navigate(`/study/session?deck=${deckId}`);
  const startDuel = (deckId) => navigate(`/study/duel?deck=${deckId}`);

  return (
    <div style={paperBackground()} data-testid="study-hub-page">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>
        <header style={{ marginBottom: 40 }}>
          <div style={sectionLabel}>Institute of Analytics · Active Recall</div>
          <h1 style={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: 44, lineHeight: 1.1, margin: '4px 0 8px',
            fontWeight: 700, color: STUDY_THEME.ink,
          }}>
            Your Study Decks
          </h1>
          <p style={{
            fontSize: 16, color: STUDY_THEME.inkSoft, maxWidth: 720,
            lineHeight: 1.55, margin: 0,
          }}>
            Spaced repetition, concept bridges, and Feynman prompts — designed
            for the 11 PM pre-exam brain, not the 3 PM lecture. Everything
            resurfaces at the interval that keeps it durable.
          </p>
        </header>

        {err && (
          <div style={{
            padding: 20, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 24,
          }}>
            {err}
          </div>
        )}

        {!hub ? (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Loading your decks…
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 24,
            alignItems: 'start',
          }}>
            {/* Center — decks */}
            <div style={cardStyle}>
              <div style={sectionLabel}>
                {hub.cards_due_total > 0
                  ? `${hub.cards_due_total} cards due today`
                  : 'No cards due — you\'re all caught up'}
              </div>
              {genErr && (
                <div style={{
                  padding: 12, borderRadius: 8, background: '#FEE2E2',
                  color: '#991B1B', fontSize: 13, marginBottom: 12,
                }} data-testid="regen-error">{genErr}</div>
              )}
              {generating && (
                <div style={{
                  padding: 12, borderRadius: 8, background: 'rgba(252,211,89,0.20)',
                  color: STUDY_THEME.goldDeep, fontSize: 13, marginBottom: 12,
                }} data-testid="regen-progress">
                  🌀 Regenerating cards for <b>{generating}</b> using Claude Haiku… (~15-30s)
                </div>
              )}
              {hub.decks.length === 0 ? (
                <div style={{
                  padding: 40, textAlign: 'center', color: STUDY_THEME.muted,
                  border: `2px dashed ${STUDY_THEME.line}`, borderRadius: 12,
                }}>
                  <div style={{ fontSize: 28, marginBottom: 12 }}>📚</div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>No decks yet</div>
                  <div style={{ fontSize: 13 }}>
                    A default Week 1 deck will appear here on your next login.
                  </div>
                </div>
              ) : (
                hub.decks.map((deck) => (
                  <DeckRow
                    key={deck.id}
                    deck={deck}
                    onStart={startSession}
                    onDuel={startDuel}
                    onBuddyInvite={setInviteDeck}
                    canGenerate={canGenerate}
                    onRegenerate={(d) => {
                      // For auto-generated decks: id encodes module (auto_deck_<module_id>).
                      // For seed deck: fall back to nothing (skip).
                      const mid = d.id?.startsWith('auto_deck_')
                        ? d.id.replace('auto_deck_', '')
                        : null;
                      if (mid) generateFor(mid);
                    }}
                    isRegenerating={generating === (deck.id?.startsWith('auto_deck_') ? deck.id.replace('auto_deck_','') : '')}
                  />
                ))
              )}
            </div>

            {/* Right rail */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <StreakCard streak={hub.streak_days} />

              <button
                onClick={() => navigate('/study/duels')}
                data-testid="buddy-duels-tile"
                style={{
                  ...cardStyle, textAlign: 'left', border: 'none', width: '100%',
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: inboxSummary.incoming_count > 0
                    ? 'linear-gradient(135deg, rgba(252,211,89,0.35), white 70%)'
                    : 'white',
                }}
              >                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={sectionLabel}>🥊 Buddy Duels</div>
                  {inboxSummary.incoming_count > 0 && (
                    <span data-testid="incoming-badge" style={{
                      padding: '3px 10px', borderRadius: 999,
                      background: STUDY_THEME.goldDark, color: 'white',
                      fontSize: 11, fontWeight: 700,
                    }}>{inboxSummary.incoming_count} new</span>
                  )}
                </div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 22,
                  fontWeight: 700, color: STUDY_THEME.ink, margin: '6px 0 4px',
                }}>
                  {inboxSummary.incoming_count > 0
                    ? 'Someone challenged you'
                    : inboxSummary.active_count > 0
                      ? `${inboxSummary.active_count} in progress`
                      : 'Challenge a colleague'}
                </div>
                <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft }}>
                  Peer duels · same cards, race-scored, async play →
                </div>
              </button>

              <button
                onClick={() => navigate('/study/concept-map')}
                data-testid="concept-map-tile"
                style={{
                  ...cardStyle, textAlign: 'left', border: 'none', width: '100%',
                  cursor: 'pointer', fontFamily: 'inherit', background: 'white',
                }}
              >
                <div style={sectionLabel}>🕸 Concept Map</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 22,
                  fontWeight: 700, color: STUDY_THEME.ink, margin: '6px 0 4px',
                }}>See how it all connects</div>
                <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft }}>
                  Interactive graph · nodes coloured by your mastery →
                </div>
              </button>

              <button
                onClick={() => navigate('/study/insights')}
                data-testid="insights-tile"
                style={{
                  ...cardStyle, textAlign: 'left', border: 'none', width: '100%',
                  cursor: 'pointer', fontFamily: 'inherit', background: 'white',
                }}
              >
                <div style={sectionLabel}>🧠 Your Insights</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 22,
                  fontWeight: 700, color: STUDY_THEME.ink, margin: '6px 0 4px',
                }}>Recall · Connect · Streak</div>
                <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft }}>
                  Mastery trend, weakest cards, peer rating →
                </div>
              </button>

              <div style={cardStyle}>
                <div style={sectionLabel}>How it works</div>
                <ol style={{
                  paddingLeft: 20, fontSize: 13.5, lineHeight: 1.6,
                  color: STUDY_THEME.inkSoft, margin: 0,
                }}>
                  <li style={{ marginBottom: 6 }}>
                    Rate each card <b>Again · Hard · Good · Easy</b>.
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    Cards you rate <b>Good/Easy</b> resurface later
                    (SM-2 spacing).
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    <b>🌉 Bridge</b> cards connect concepts across modules.
                  </li>
                  <li>
                    Try <b>⚔️ AI</b> or <b>🥊 Colleague</b> duels — same cards,
                    race scoring.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {inviteDeck && (
          <InviteBuddyModal
            deck={inviteDeck}
            onClose={() => setInviteDeck(null)}
          />
        )}
      </div>
    </div>
  );
}
